/**
 * Client for the sign-in tables (relay/leaderboardTable.ts): the host opens a table and starts the game from it; every other
 * player joins from their OWN phone with their own nickname and PIN, so a PIN is never typed on someone else's screen.
 *
 * Two kinds of caller live here. The host's calls carry the host's activated session (like every other leaderboard call). A
 * guest's phone has NO session and needs no activation code — it only knows the table's link token, which the relay accepts
 * for that one table and nothing else. Nothing here stores a PIN; they are typed, sent once and dropped.
 */

import type { Coordinates } from './activation';
import {
  MSG_NETWORK,
  MSG_UNAVAILABLE,
  describeStartError,
  relayRequest,
  type LeaderboardPeriodInfo,
  type SeatNotCounted,
  type StartedLeaderboardGame,
} from './leaderboardClient';

const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

export interface TableMember {
  nickname: string;
  isHost: boolean;
}

/** What a phone polling a table's link sees. An unknown, expired or cancelled table is simply `closed`. */
export type TableView =
  | { status: 'open'; members: TableMember[]; expiresAt: number; consentPeriod: string; approvalRequired: boolean }
  /** Sent to the staff: every phone at the table is locked until they answer. */
  | { status: 'awaiting_approval'; tableName: string; players: string[] }
  | { status: 'declined' }
  /** The game exists; the host has yet to open it (`byStaff` false = no approval was needed). */
  | { status: 'approved'; byStaff: boolean }
  /** The host opened the game: `shareToken` is its guest link, which the guests' phones open. */
  | { status: 'started'; shareToken: string | null }
  | { status: 'closed' };

type TableFailure = { ok: false; error: string; code?: string; player?: string };

/** How many players a table seats (mirrors `PLAYERS_PER_GAME` in relay/leaderboard.ts). */
export const TABLE_SIZE = 4;

/** The link a guest opens: the app's own address with the table's token after `#table=`. */
export function buildTableLink(token: string): string {
  const url = new URL(window.location.href);
  url.hash = `table=${token}`;
  return url.toString();
}

/** The token in `#table=…`, or undefined when the address is not a table link. */
export const TABLE_HASH_PREFIX = '#table=';
export function tableTokenFromHash(hash: string): string | undefined {
  if (!hash.startsWith(TABLE_HASH_PREFIX)) return undefined;
  const token = decodeURIComponent(hash.slice(TABLE_HASH_PREFIX.length)).trim();
  return token || undefined;
}

// --- a guest's consent, remembered for the day -------------------------------------------------------------------------------

const GUEST_CONSENT_KEY = 'mahjong-scorer:tableConsentPeriod';

/**
 * A guest agrees once a day, like the host does when they enter the activation code: the relay names the current "consent
 * period" (it changes when the activation code does), and this phone remembers the one it agreed in. An empty period is never
 * remembered, so a relay that cannot name one simply asks every time. Storage can be blocked (private windows), so every
 * access is guarded and a failure just means asking again.
 */
export function guestConsentGiven(period: string): boolean {
  if (!period) return false;
  try {
    return localStorage.getItem(GUEST_CONSENT_KEY) === period;
  } catch {
    return false;
  }
}

export function rememberGuestConsent(period: string): void {
  if (!period) return;
  try {
    localStorage.setItem(GUEST_CONSENT_KEY, period);
  } catch {
    // Not remembered: they will be asked again next time, which is the safe direction.
  }
}

// --- messages -----------------------------------------------------------------------------------------------------------------

/**
 * What to tell a player (or the host) when a table step fails. Never says how long a lockout lasts (the project's standing rule
 * for security messages). The PIN-related codes speak to the person holding the phone, so "you" rather than a name.
 */
export function describeTableError(code: string, serverMessage: string, player?: string): string {
  switch (code) {
    case 'consent_required':
      return 'Please tick the box to agree before continuing.';
    case 'table_closed':
      return 'This table is no longer open. Ask the host to open a new one.';
    case 'table_full':
      return 'This table already has four players.';
    case 'not_host':
      return 'Only the phone that opened this table can do that.';
    case 'table_locked':
      return 'This table has been sent to the staff for approval, so nobody can join or leave it now.';
    case 'not_ready':
      return 'The staff have not approved this table yet.';
    case 'not_pending':
      return 'That table is not waiting for approval any more.';
    case 'unknown':
      return 'That nickname is not registered yet. Choose "I’m new" to create a User ID.';
    case 'wrong_pin':
      return 'That PIN is not right.';
    case 'locked':
      return 'Too many wrong attempts. Ask the staff for help.';
    case 'pending':
      return `${player ?? 'This player'} is waiting for staff approval. Once the staff approve the request, sign in again.`;
    case 'reset_expired':
      return 'The PIN reset has expired. Ask the staff to unlock your account again.';
    case 'rate_limited':
      return 'Too many requests from this table. Ask the staff for help.';
    default:
      // Shared codes (no period, not at the venue, an unregistered seat...) keep the wording used everywhere else.
      return describeStartError(code, serverMessage, player);
  }
}

// --- the host's calls (with the host's session) ----------------------------------------------------------------------------

const asFailure = (json: Record<string, unknown>): TableFailure => {
  const code = typeof json.code === 'string' ? json.code : '';
  const player = typeof json.player === 'string' ? json.player : undefined;
  return { ok: false, code, player, error: describeTableError(code, String(json.error ?? ''), player) };
};

export async function openTableApi(
  nickname: string,
  pin: string,
  coords?: Coordinates,
): Promise<{ ok: true; token: string; expiresAt: number; members: TableMember[]; approvalRequired: boolean } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/open', { nickname, pin, ...(coords ?? {}) });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  return {
    ok: true,
    token: String(res.json.token),
    expiresAt: Number(res.json.expiresAt),
    members: Array.isArray(res.json.members) ? (res.json.members as TableMember[]) : [],
    approvalRequired: res.json.approvalRequired !== false,
  };
}

export async function removeTableMemberApi(token: string, nickname: string): Promise<{ ok: true } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/remove', { token, nickname });
  if (!('status' in res)) return res;
  return res.ok ? { ok: true } : asFailure(res.json);
}

export async function closeTableApi(token: string): Promise<{ ok: true } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/close', { token });
  if (!('status' in res)) return res;
  return res.ok ? { ok: true } : asFailure(res.json);
}

/**
 * The host's button. With staff approval on, the table is only SENT to the staff (`pending: true`, nothing starts yet); with it off
 * the game starts at once. The relay re-checks who can still sit either way.
 */
export async function startTableGame(
  token: string,
  players: string[],
  opts: { tableName: string; seating: 'entered' | 'dice' },
  coords?: Coordinates,
): Promise<{ ok: true; pending: true } | { ok: true; pending: false; game: StartedLeaderboardGame } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/start', { token, players, tableName: opts.tableName, seating: opts.seating, ...(coords ?? {}) });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  if (res.json.pending === true) return { ok: true, pending: true };
  return { ok: true, pending: false, game: gameFrom(res.json) };
}

const gameFrom = (json: Record<string, unknown>): StartedLeaderboardGame => ({
  gameId: String(json.gameId),
  period: json.period as LeaderboardPeriodInfo,
  players: json.players as string[],
  notCounted: (json.notCounted as SeatNotCounted[] | undefined) ?? [],
});

/** The host collects the game the staff approved, to open it on their phone. */
export async function fetchApprovedGame(token: string): Promise<{ ok: true; game: StartedLeaderboardGame } | TableFailure> {
  const res = await relayRequest('GET', `/leaderboard/table/game?token=${encodeURIComponent(token)}`, undefined, true);
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  return { ok: true, game: gameFrom(res.json) };
}

/** The host has opened the game: its guest link goes to the guests' phones through the table. */
export async function postTableShare(token: string, shareToken: string): Promise<{ ok: true } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/share', { token, shareToken });
  if (!('status' in res)) return res;
  return res.ok ? { ok: true } : asFailure(res.json);
}

/** After the staff declined the table, the host takes it back: everyone is still signed in. */
export async function reopenTableApi(token: string): Promise<{ ok: true; members: TableMember[] } | TableFailure> {
  const res = await relayRequest('POST', '/leaderboard/table/reopen', { token });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  return { ok: true, members: Array.isArray(res.json.members) ? (res.json.members as TableMember[]) : [] };
}

// --- a guest's phone (no session) -------------------------------------------------------------------------------------------

type GuestReply = { ok: boolean; status: number; json: Record<string, unknown> };

async function guestRequest(method: 'GET' | 'POST', path: string, body?: unknown): Promise<GuestReply | TableFailure> {
  let res: Response;
  try {
    res = await fetch(`${RELAY_BASE}${path}`, {
      method,
      ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      cache: 'no-store',
    });
  } catch {
    return { ok: false, error: MSG_NETWORK };
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 503) return { ok: false, error: MSG_UNAVAILABLE };
  return { ok: res.ok, status: res.status, json };
}

/** The table as it is now. Anyone holding the link can read it: names only, no PINs. */
export async function fetchTableState(token: string): Promise<{ ok: true; view: TableView } | { ok: false; error: string; code?: string }> {
  const res = await guestRequest('GET', `/leaderboard/table/state?token=${encodeURIComponent(token)}`);
  if (!('status' in res)) return { ok: false, error: res.error };
  if (!res.ok) {
    // `code: 'disabled'` = an admin has switched the leaderboard off — not a connection problem, so the caller can say so.
    return { ok: false, error: String(res.json.error ?? MSG_UNAVAILABLE), ...(res.json.code === 'disabled' ? { code: 'disabled' } : {}) };
  }
  const status = res.json.status;
  if (status === 'open') {
    return {
      ok: true,
      view: {
        status: 'open',
        members: Array.isArray(res.json.members) ? (res.json.members as TableMember[]) : [],
        expiresAt: Number(res.json.expiresAt),
        consentPeriod: typeof res.json.consentPeriod === 'string' ? res.json.consentPeriod : '',
        approvalRequired: res.json.approvalRequired !== false,
      },
    };
  }
  if (status === 'awaiting_approval') {
    return {
      ok: true,
      view: {
        status: 'awaiting_approval',
        tableName: String(res.json.tableName ?? ''),
        players: Array.isArray(res.json.players) ? (res.json.players as string[]) : [],
      },
    };
  }
  if (status === 'declined') return { ok: true, view: { status: 'declined' } };
  if (status === 'approved') return { ok: true, view: { status: 'approved', byStaff: res.json.byStaff === true } };
  if (status === 'started') {
    return { ok: true, view: { status: 'started', shareToken: typeof res.json.shareToken === 'string' && res.json.shareToken ? res.json.shareToken : null } };
  }
  return { ok: true, view: { status: 'closed' } };
}

export interface JoinedTable {
  ok: true;
  nickname: string;
  members: TableMember[];
  /** Set when this game will not count toward the board for this player (they are at a limit). */
  notCounted?: 'daily_limit' | 'period_limit';
}

/** Signs in to the table with an existing User ID. `agreed` is the ticked consent box, asked for every time. */
export async function joinTableApi(token: string, nickname: string, pin: string, agreed: boolean): Promise<JoinedTable | TableFailure> {
  const res = await guestRequest('POST', '/leaderboard/table/join', { token, nickname, pin, agreed });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  const notCounted = res.json.notCounted === 'daily_limit' || res.json.notCounted === 'period_limit' ? res.json.notCounted : undefined;
  return {
    ok: true,
    nickname: String(res.json.nickname ?? nickname),
    members: Array.isArray(res.json.members) ? (res.json.members as TableMember[]) : [],
    ...(notCounted ? { notCounted } : {}),
  };
}

/** A new player asks to join. 'pending' = the request is with the staff; 'approved' = they can sign in now (approval is off). */
export async function registerAtTableApi(
  token: string,
  nickname: string,
  pin: string,
  agreed: boolean,
): Promise<{ ok: true; nickname: string; status: 'pending' | 'approved' } | TableFailure> {
  const res = await guestRequest('POST', '/leaderboard/table/register', { token, nickname, pin, agreed });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  return { ok: true, nickname: String(res.json.nickname ?? nickname), status: res.json.status === 'pending' ? 'pending' : 'approved' };
}

/** Choosing a PIN of their own on their phone, after staff unlocked the account. */
export async function changePinAtTableApi(
  token: string,
  nickname: string,
  pin: string,
  newPin: string,
): Promise<{ ok: true; nickname: string } | TableFailure> {
  const res = await guestRequest('POST', '/leaderboard/table/change-pin', { token, nickname, pin, newPin });
  if (!('status' in res)) return res;
  if (!res.ok) return asFailure(res.json);
  return { ok: true, nickname: String(res.json.nickname ?? nickname) };
}
